import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { articles } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareBar from "@/components/feature/ShareBar";
import ImagePreview from "@/components/feature/ImagePreview";
import RelatedList from "@/components/feature/RelatedList";
import HotRanking from "@/components/feature/HotRanking";
import CommentSection from "@/components/feature/CommentSection";
import Toast from "@/components/base/Toast";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { applySort, formatViews, getCategoryName, newsTypeLabels } from "@/utils/news";

export default function DetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const article = articles.find((a) => a.id === id);

  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [toast, setToast] = useState("");

  const gallery = useMemo(() => {
    if (!article) return [];
    return article.images.length > 0 ? article.images : [article.cover];
  }, [article]);

  const related = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 5);
  }, [article]);

  const hot = useMemo(
    () => applySort(articles.filter((a) => a.id !== id), "hot").slice(0, 6),
    [id],
  );

  usePageMeta({
    title: article ? `${article.title} - 51爆料网` : "爆料详情 - 51爆料网",
    description: article ? article.summary : "51爆料网爆料详情页。",
    keywords: article
      ? `${article.title},${getCategoryName(article.category)},51爆料网,爆料新闻`
      : "51爆料网,爆料详情",
  });

  if (!article) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          title="未找到该条爆料"
          message="内容可能已被移除或链接有误，你可以返回首页查看更多最新爆料。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = getCategoryName(article.category);

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-4 md:px-6 md:py-5">
          <Breadcrumb
            items={[
              { label: "爆料分类" },
              { label: categoryName, to: `/category/${article.category}` },
              { label: "正文" },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          <article className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Link
                to={`/category/${article.category}`}
                className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line" />
                {categoryName}
              </Link>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-700">
                {article.region}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-accent-700">
                {newsTypeLabels[article.type]}
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.publishTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-voice-line" />
                来源：{article.source}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line" />
                {article.comments} 评论
              </span>
            </div>

            <div className="mt-6 h-[240px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 51爆料网`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((para, i) => (
                <p key={i} className="text-[15px] leading-8 text-foreground-700 md:text-base">
                  {para}
                </p>
              ))}
            </div>

            {article.type === "video" && (
              <div className="mt-8">
                <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">现场视频</h2>
                <div className="relative flex h-[240px] w-full items-center justify-center overflow-hidden rounded-lg bg-foreground-950 md:h-[400px]">
                  <img
                    src={article.cover}
                    alt={`${article.title} 视频封面`}
                    className="absolute inset-0 h-full w-full object-cover object-top opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setToast("视频播放功能开发中，暂不支持真实播放")}
                    className="relative flex h-16 w-16 items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform hover:scale-105"
                  >
                    <i className="ri-play-fill text-3xl" />
                  </button>
                  <span className="absolute bottom-4 left-4 rounded-full bg-foreground-950/70 px-3 py-1 text-xs text-background-50">
                    演示播放器 · 功能开发中
                  </span>
                </div>
              </div>
            )}

            {gallery.length > 1 && (
              <div className="mt-8">
                <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">
                  现场图片（{gallery.length}）
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {gallery.map((img, i) => (
                    <button
                      key={img}
                      type="button"
                      onClick={() => {
                        setPreviewIndex(i);
                        setPreviewOpen(true);
                      }}
                      className="group h-28 overflow-hidden rounded-lg bg-background-200 md:h-36"
                    >
                      <img
                        src={img}
                        alt={`${article.title} 图片${i + 1}`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-foreground-400">点击图片可放大预览，支持左右切换</p>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-y border-background-200 py-5">
              <ShareBar title={article.title} onCopied={setToast} />
              <button
                type="button"
                onClick={() => navigate(`/category/${article.category}`)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回{categoryName}列表
              </button>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <CommentSection count={article.comments} />
          </article>

          <aside className="w-full shrink-0 space-y-6 lg:w-[340px]">
            <RelatedList title="相关推荐" list={related} />
            <HotRanking list={hot} />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="mb-3 font-heading text-base font-bold text-foreground-950">更多分类</h3>
              <div className="flex flex-wrap gap-2">
                {[article.category, "society", "livelihood", "tech"]
                  .filter((v, i, arr) => arr.indexOf(v) === i)
                  .map((cid) => (
                    <Link
                      key={cid}
                      to={`/category/${cid}`}
                      className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                    >
                      {getCategoryName(cid)}
                    </Link>
                  ))}
                <Link
                  to="/category/all"
                  className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  全部爆料
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {previewOpen && (
        <ImagePreview
          images={gallery}
          index={previewIndex}
          onClose={() => setPreviewOpen(false)}
          onChange={setPreviewIndex}
        />
      )}

      <Toast message={toast} visible={!!toast} onClose={() => setToast("")} />
    </div>
  );
}